import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { Banner } from '../../Auth/Banner/Banner';
import { BannerShopify } from '../../Auth/Banner/BannerShopify';
import { AnimatedThemeToggler } from '../../Shared/ThemeSelect/AnimatedThemeToggler';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';
import { PostHogFeatureFlagEnum, SignupBackgroundVariant } from '@/Domain/enum/PostHogEvent.enum';

// Local static asset - place image at: backend/appquickchat/appquickchat/static/assets/app-blurred-bg.png
const BLURRED_BACKGROUND_URL = '/static/assets/app-blurred-bg.png';

export default function EntryLayout() {
  const location = useLocation();
  const { page } = useAnalytics();

  // A/B Test: signup_background_test - returns 'control', 'blur', or null
  // Disabled for Shopify users (always show white background)
  const backgroundVariant = useFeatureFlagVariantKey(PostHogFeatureFlagEnum.SignupBackgroundTest);
  const isShopifyUser = location.search.includes('shopify');
  const isBlurredBackground = backgroundVariant === SignupBackgroundVariant.Blur && !isShopifyUser;

  useEffect(() => {
    page({
      path: location.pathname,
      search: location.search,
      url: window.location.origin + location.pathname + location.search + location.hash,
    });
  }, [location]);

  return (
    <div className="relative flex min-h-screen min-w-screen bg-background text-foreground">
      <div className="absolute top-6 right-6 z-20">
        <AnimatedThemeToggler />
      </div>

      {/* A/B Test: App screenshot background for 'blur' variant */}
      {isBlurredBackground && (
        <>
          {/* Background image layer */}
          <div
            className="fixed inset-0 -z-20"
            style={{
              backgroundImage: `url("${BLURRED_BACKGROUND_URL}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'left top',
              filter: 'blur(2px)',
            }}
          />
          {/* Dark overlay layer */}
          <div
            className="fixed inset-0 -z-10"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
            }}
          />
        </>
      )}

      <div
        className={`w-full ${location.search.includes('shopify') ? 'lg:w-[55%]' : ''} flex items-center justify-center ${
          isBlurredBackground ? 'bg-transparent' : 'bg-background'
        }`}
      >
        <div className="w-[450px]">
          <div
            className={`px-11 pb-5 pt-11 ${
              isBlurredBackground
                ? 'my-8 rounded-2xl border border-border bg-card/95 shadow-lg backdrop-blur-sm'
                : ''
            }`}
          >
            {!isBlurredBackground && (
              <a href="https://quickchat.ai">
                <QuickchatAiIcon className="scale-[1.2] absolute top-6 left-6" />
              </a>
            )}
            <Outlet />
            <small className="mt-7 block text-center text-muted-foreground">
              © {new Date().getFullYear()} Incentivai Inc.
            </small>
          </div>
        </div>
      </div>
      <div
        className={`hidden ${location.search.includes('shopify') ? 'lg:flex' : ''} w-[45%] justify-end`}
      >
        {location.search.includes('shopify') ? <BannerShopify /> : <Banner />}
      </div>
    </div>
  );
}
